import React, {
  FC,
  Fragment,
  MutableRefObject,
  useCallback,
  useContext,
  useRef,
} from "react";
import { Pagination, Table } from "rsuite";
import { useTableSize } from "../../../../hook/width";
import { TableInstance } from "rsuite/Table";
// import { TableHeaderStyle } from "../../../components/table/style";
import { TableColumnContext } from "../../../context/TableContext";
import { PatrolGroupTableTimeCell } from "./time";
import { PatrolGroupTableOperatorCell } from "./operator";
import { PatrolGroupTableUnityCell } from "./unity";
import { UserPatrol } from "../../../../domain/patrol/Patrol";
import { PatrolGroupTableZoneCell } from "./zone";
import { PatrolGroupTableIncidentCell } from "./incident";
import { PatrolGroupTableActionCell } from "./action";
import { EmptyState } from "../../../components/state/empty";
import { useDataPager } from "../../../../hook/pager";
import { PatrolTableColumns } from "../../../../schema/table/PatrolTable";

import { useStyleContext } from "../../../context/StyleContext";

type Props = {
  userPatrolList?: UserPatrol[];
  onSelect: (item: UserPatrol) => void;
  onPreviewClicked: (item: UserPatrol) => void;
  name: string;
};

export const PatrolTable: FC<Props> = ({
  userPatrolList,
  onSelect,
  onPreviewClicked,
  name,
}) => {
  const actionRef = useRef<string | null>(null) as MutableRefObject<
    string | null
  >;
  const tableRef = useRef<TableInstance<any, any>>(null);
  const tableSize = useTableSize(tableRef);
  const tableContext = useContext(TableColumnContext);
  const tablePreferences =
    tableContext.getTablePreferences<PatrolTableColumns>(name);

  const {
    tableHeaderStyle,
    tableHeaderStyleSecondary,
    stylePage,
    primaryColors,
  } = useStyleContext();

  /* eslint-disable */
  const { partialData, changePage, page, changeLimit, limits, limit } =
    useDataPager({
      id: `pat_table_${name}`,
      items: userPatrolList,
    });

  const onRowSelect = useCallback(
    (item: UserPatrol) => {
      if (actionRef.current) onPreviewClicked(item);
      else onSelect(item);
      actionRef.current = null;
    },
    [partialData]
  );

  const onPreviewActionSelected = useCallback(() => {
    actionRef.current = "preview";
  }, [partialData]);

  /* eslint-enable */
  return (
    <Fragment>
      <Table
        autoHeight
        data={partialData}
        ref={tableRef}
        onRowClick={onRowSelect as any}
        renderEmpty={() => (
          <EmptyState
            title={"No hay rondas"}
            message={
              "No se encontraron reportes de rondas para la fecha seleccionada."
            }
          />
        )}
        className={"rounded md:rounded-xl"}
        cellBordered
      >
        {tablePreferences.map((column) => {
          switch (column.name) {
            case "time":
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(0.1)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Hora
                  </Table.HeaderCell>
                  <PatrolGroupTableTimeCell />
                </Table.Column>
              );
            case "operator":
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(0.3)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Operador
                  </Table.HeaderCell>
                  <PatrolGroupTableOperatorCell />
                </Table.Column>
              );
            case "unity":
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(0.3)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Unidad
                  </Table.HeaderCell>
                  <PatrolGroupTableUnityCell />
                </Table.Column>
              );
            case "zone":
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(0.1)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Zona
                  </Table.HeaderCell>
                  <PatrolGroupTableZoneCell />
                </Table.Column>
              );
            case "incident":
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(0.1)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Incidente
                  </Table.HeaderCell>
                  <PatrolGroupTableIncidentCell />
                </Table.Column>
              );
            default:
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(0.1)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Acciones
                  </Table.HeaderCell>
                  <PatrolGroupTableActionCell
                    onPreviewActionClicked={onPreviewActionSelected}
                  />
                </Table.Column>
              );
          }
        })}
      </Table>
      {userPatrolList ? (
        <div className={"py-4 flex flex-col gap-4"}>
          <span className={"text-md"}>
            Mostrando {partialData?.length} elementos / Total{" "}
            {userPatrolList.length}
          </span>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["limit", "|", "pager", "skip"]}
            total={userPatrolList.length}
            limitOptions={limits}
            limit={limit}
            activePage={page}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
          />
        </div>
      ) : null}
    </Fragment>
  );
};
